/**
 * Session Events — lightweight utility for emitting structured events into
 * the session_events table (migration 311).
 *
 * All live-session coordination (waiting room gate, item spotlight, broadcast,
 * pause/resume) flows through this table so participants receive updates via
 * Supabase Realtime postgres_changes subscriptions without needing custom
 * WebSocket infrastructure.
 *
 * Known event_type values:
 *   waiting_room_started  — facilitator admitted participants; WaitingRoom advances
 *   session_paused        — tool frozen; participants see paused overlay
 *   session_resumed       — tool unfrozen
 *   item_spotlighted      — payload: { itemId, itemTitle, note? }
 *   item_spotlight_cleared — spotlight dismissed
 *   broadcast_message     — payload: { message }
 */

import { getSafeSupabaseClient, getCurrentWorkspaceId, generateUUID } from './supabaseSync';

export interface SessionEventPayload {
  [key: string]: unknown;
}

export async function emitSessionEvent(
  sessionId: string,
  actorId: string,
  actorName: string,
  eventType: string,
  payload: SessionEventPayload = {},
): Promise<{ success: boolean; error?: string }> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { success: false, error: 'No Supabase client' };

  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { success: false, error: 'No workspace' };

  const { error } = await supabase.from('session_events').insert({
    id: generateUUID(),
    session_id: sessionId,
    workspace_id: workspaceId,
    actor_id: actorId,
    actor_name: actorName,
    event_type: eventType,
    payload,
  });

  if (error) {
    console.error(`[sessionEvents] Failed to emit ${eventType}:`, error.message);
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * The newest of these events for a session, so a screen opened late starts
 * where the room is (a pause in force, a spotlight up) instead of waiting for
 * the next event. Null when there is none or it cannot be read.
 */
export async function fetchLatestSessionEvent(
  sessionId: string,
  eventTypes: readonly string[],
): Promise<{ event_type: string; payload: SessionEventPayload } | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase || !sessionId || eventTypes.length === 0) return null;
  const { data, error } = await supabase
    .from('session_events')
    .select('event_type, payload')
    .eq('session_id', sessionId)
    .in('event_type', [...eventTypes])
    .order('created_at', { ascending: false })
    .limit(1);
  if (error || !data || data.length === 0) return null;
  return data[0] as { event_type: string; payload: SessionEventPayload };
}

